import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, DownloadIcon, FilterIcon, CheckCircle2Icon } from 'lucide-react';
import { mockShifts, ShiftStatus } from '../mocks/shiftData';
import { formatMoney } from '../../../shared/config/localeProfile';
export function ShiftsList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const getStatusColor = (status: ShiftStatus) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'variance':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending_settlement':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'settled':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const formatStatus = (status: string) => {
    return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-10764">
      <div className="flex items-center justify-between" data-id="element-10765">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-10766">Shifts</h1>
        <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-10767">
          <DownloadIcon size={16} className="mr-2" data-id="element-10768" />
          Export
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-10769">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-4 bg-gray-50/50 sticky top-0 z-10" data-id="element-10770">
          <div className="relative flex-1 min-w-[240px]" data-id="element-10771">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-10772" />
            <input type="text" placeholder="Search shifts, operators, or terminals..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-10773" />
          </div>
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-id="element-10774">
            <option data-id="element-10775">All Stores</option>
            <option data-id="element-10776">Bila Bila Mart — KLCC</option>
            <option data-id="element-10777">Bila Bila Mart — Bangsar</option>
          </select>
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-id="element-10778">
            <option data-id="element-10779">All Statuses</option>
            <option data-id="element-10780">Open</option>
            <option data-id="element-10781">Closed</option>
            <option data-id="element-10782">Variance</option>
            <option data-id="element-10783">Pending Settlement</option>
            <option data-id="element-10784">Settled</option>
          </select>
          <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-10785">
            <FilterIcon size={16} className="mr-2 text-gray-500" data-id="element-10786" />
            More Filters
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-10787">
          <table className="w-full text-left border-collapse" data-id="element-10788">
            <thead data-id="element-10789">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-10790">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10791">
                  Shift ID
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10792">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10793">
                  Store & Terminal
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10794">
                  Operator
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10795">
                  Opened / Closed
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10796">
                  Txns
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10797">
                  Net Value
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10798">
                  Variance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-10799">
              {mockShifts.map(shift => <tr key={shift.id} onClick={() => navigate(`/shifts/${shift.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-10800">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10801">
                    <div className="flex items-center" data-id="element-10802">
                      {shift.status === 'open' && <span className="relative flex h-2 w-2 mr-2" data-id="element-10803">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-10804"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" data-id="element-10805"></span>
                        </span>}
                      <span className="text-sm font-medium text-gray-900" data-id="element-10806">
                        {shift.id}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10807">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(shift.status)}`} data-id="element-10808">
                      {formatStatus(shift.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10809">
                    <div className="text-sm text-gray-900" data-id="element-10810">{shift.store}</div>
                    <div className="text-xs text-gray-500" data-id="element-10811">
                      {shift.terminalId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10812">
                    <div className="text-sm text-gray-900" data-id="element-10813">
                      {shift.operator}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-10814">
                    <div className="text-sm text-gray-900" data-id="element-10815">
                      {new Date(shift.openedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-10816">
                      {shift.closedAt ? new Date(shift.closedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : `Live (${shift.duration})`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-mono text-sm text-gray-900" data-id="element-10817">
                    {shift.txnCounts}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-mono text-sm text-gray-900" data-id="element-10818">
                    {formatMoney(shift.netValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-mono text-sm" data-id="element-10819">
                    {shift.variance === null ? <span className="text-gray-400" data-id="element-10820">-</span> : shift.variance === 0 ? <span className="text-green-600 flex items-center justify-end" data-id="element-10821">
                        <CheckCircle2Icon size={14} className="mr-1" data-id="element-10822" /> Balanced
                      </span> : <span className={shift.variance > 0 ? 'text-green-600' : 'text-red-600'} data-id="element-10823">
                        {shift.variance > 0 ? '+' : ''}
                        {formatMoney(shift.variance)}
                      </span>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}